import React, { useState } from "react";
import { Button, Dropdown, Form, Tag, message } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  MoreOutlined,
  ReloadOutlined,
  SwapOutlined,
  HeartOutlined,
  DisconnectOutlined,
  UndoOutlined,
} from "@ant-design/icons";
import CommonTable from "../../../../global/table";
import CommonDialog from "../../../../global/dialog";
import CommonDrawer from "../../../../global/drawer";
import CommonForm from "../../../../global/form";
import NoRecordFound from "../../../../global/noRecordFound";
import { formatDateTime } from "../../../../../utils/functions";
import { interceptor } from "../../../../../utils/interceptor";
import SOURCES from "../../../../../utils/constants";
import EnhancedStaticResidentialIpsGridView from "./enhancedStaticResidentialIpsGridView";
import EnhancedStaticResidentialIpDetails from "./enhancedStaticResidentialIpDetails";
import EnhancedAddStaticResidentialIp from "./enhancedAddStaticResidentialIp";

const STATUS_COLORS = {
  unassigned: "default",
  active: "green",
  flagged: "orange",
  suspended: "red",
  retired: "red",
};

const STATUS_OPTIONS = [
  { label: "Unassigned", value: "unassigned" },
  { label: "Active", value: "active" },
  { label: "Flagged", value: "flagged" },
  { label: "Suspended", value: "suspended" },
];

const REASSIGN_REASON_OPTIONS = [
  { label: "Failed health check / not working", value: "health_failed" },
  { label: "Not found in Webshare account", value: "not_found" },
  { label: "Blocked or challenged by target site", value: "blocked" },
  { label: "Slow or unstable connection", value: "performance" },
  { label: "Permanently suspend this IP", value: "suspended" },
  { label: "Other / manual replacement", value: "other" },
];

const isUnhealthy = (record) =>
  record.status === "flagged" || record.lastHealthCheckResult === "fail";

const callProxyApi = async (method, url, data) => {
  const axiosInstance = interceptor();
  const res = await axiosInstance({ method, url, data });
  return res.data;
};

const EnhancedStaticResidentialIpsList = ({ staticResidentialIps, viewType, refetch, onAssignClick }) => {
  const [statusForm] = Form.useForm();
  const [reassignForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [statusTarget, setStatusTarget] = useState(null);
  const [reassignTarget, setReassignTarget] = useState(null);
  const [unassignTarget, setUnassignTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [details, setDetails] = useState({ show: false, data: null });
  const [healthCheckingId, setHealthCheckingId] = useState(null);
  const [replacingId, setReplacingId] = useState(null);

  const handleError = (err) => {
    message.error(err?.response?.data?.data?.error || err?.message || "Something went wrong");
  };

  const handleSuspend = async () => {
    try {
      setLoading(true);
      await callProxyApi("post", `admin/proxies/${suspendTarget._id}/suspend`);
      message.success("IP suspended");
      setSuspendTarget(null);
      refetch();
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleReplaceIp = async () => {
    const proxyId = replaceTarget._id;
    try {
      setLoading(true);
      setReplacingId(proxyId);

      const start = await callProxyApi("post", `admin/proxies/${proxyId}/replace-ip`);
      const startPayload = start?.data || start || {};
      let state = startPayload.state;

      if (state === "completed") {
        message.success(startPayload.message || "IP replaced via Webshare");
        setReplaceTarget(null);
        refetch();
        return;
      }

      const maxAttempts = 30;
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        await new Promise((r) => setTimeout(r, 2000));
        const statusRes = await callProxyApi("get", `admin/proxies/${proxyId}/replace-ip/status`);
        const statusPayload = statusRes?.data || statusRes || {};
        state = statusPayload.state;

        if (state === "completed") {
          message.success(statusPayload.message || "IP replaced via Webshare");
          setReplaceTarget(null);
          refetch();
          return;
        }
        if (state === "failed") {
          throw new Error(statusPayload.message || "Webshare replacement failed");
        }
      }

      message.warning("Replacement still in progress on Webshare — check status again shortly");
      setReplaceTarget(null);
      refetch();
    } catch (err) {
      handleError(err);
      refetch();
    } finally {
      setLoading(false);
      setReplacingId(null);
    }
  };

  const handleReactivate = async (record) => {
    try {
      setLoading(true);
      await callProxyApi("post", `admin/proxies/${record._id}/reactivate`);
      message.success("IP reactivated");
      refetch();
    } catch (err) {
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    try {
      const values = await statusForm.validateFields();
      setLoading(true);
      await callProxyApi("patch", `admin/proxies/${statusTarget._id}/status`, values);
      message.success("Status updated");
      setStatusTarget(null);
      statusForm.resetFields();
      refetch();
    } catch (err) {
      if (err?.errorFields) return;
      handleError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleReassign = async () => {
    try {
      const values = await reassignForm.validateFields();
      if (!reassignTarget?._id) {
        message.error("No IP selected for reassign");
        return;
      }
      setLoading(true);
      await callProxyApi("post", `admin/proxies/${reassignTarget._id}/reassign`, {
        reason: values.reason,
      });
      message.success("IP reassigned");
      setReassignTarget(null);
      reassignForm.resetFields();
      refetch();
    } catch (err) {
      if (err?.errorFields) return;
      handleError(err);
      refetch();
    } finally {
      setLoading(false);
    }
  };

  const handleUnassign = async () => {
    try {
      if (!unassignTarget?._id) {
        message.error("No IP selected to unassign");
        return;
      }
      setLoading(true);
      await callProxyApi("post", `admin/proxies/unassign`, {
        id: unassignTarget._id,
      });
      message.success("IP unassigned and returned to the pool");
      setUnassignTarget(null);
      refetch();
    } catch (err) {
      handleError(err);
      refetch();
    } finally {
      setLoading(false);
    }
  };

  const handleHealthCheck = async (id) => {
    try {
      setHealthCheckingId(id);
      const res = await callProxyApi("post", `admin/proxies/${id}/health-check`);
      const payload = res?.data || {};
      if (payload.isValid) {
        message.success(payload.message || "Health check passed — active on Webshare");
      } else {
        message.error(payload.message || "Health check failed");
      }
      refetch();
    } catch (err) {
      handleError(err);
    } finally {
      setHealthCheckingId(null);
    }
  };

  const openDetails = (record) => setDetails({ show: true, data: record });

  const getMenuItems = (record) => {
    const unhealthy = isUnhealthy(record);
    const items = [];

    if (unhealthy && record.status !== "suspended") {
      items.push({
        key: "replace-ip",
        label: "Replace IP",
        icon: <ReloadOutlined />,
        onClick: () => setReplaceTarget(record),
      });
    }

    items.push(
      {
        key: "edit",
        label: "Edit",
        icon: <EditOutlined />,
        onClick: () => setEditTarget(record),
      },
      record.assignedUserEmail
        ? {
            key: "unassign",
            label: "Unassign IP",
            icon: <DisconnectOutlined />,
            onClick: () => setUnassignTarget(record),
          }
        : {
            key: "assign",
            label: "Assign IP",
            icon: <SwapOutlined />,
            onClick: () => onAssignClick?.(),
          },
      {
        key: "reassign",
        label: "Reassign from pool",
        icon: <ReloadOutlined />,
        disabled: !record.assignedUserEmail,
        onClick: () => setReassignTarget(record),
      },
      {
        key: "health",
        label: "Health Check",
        icon: <HeartOutlined />,
        onClick: () => handleHealthCheck(record._id),
      },
      {
        key: "status",
        label: "Change Status",
        icon: <MoreOutlined />,
        onClick: () => {
          setStatusTarget(record);
          statusForm.setFieldsValue({ status: record.status === "retired" ? "suspended" : record.status });
        },
      },
    );

    if (record.status === "suspended" || record.status === "retired") {
      items.push({
        key: "reactivate",
        label: "Reactivate",
        icon: <UndoOutlined />,
        onClick: () => handleReactivate(record),
      });
    }

    items.push({ type: "divider" });

    items.push({
      key: "suspend",
      label: "Suspend",
      icon: <DeleteOutlined />,
      danger: true,
      disabled: record.status === "suspended" || record.status === "retired",
      onClick: () => setSuspendTarget(record),
    });

    return items;
  };

  const columns = [
    {
      title: "Host",
      dataIndex: "host",
      key: "host",
      render: (text, record) => (
        <span className="text-primary font-medium">
          {text}:{record.port}
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (text) => text?.toUpperCase() || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || "default"} className="capitalize">
          {status === "retired" ? "suspended" : status || "-"}
        </Tag>
      ),
    },
    {
      title: "Assigned To",
      dataIndex: "assignedUserEmail",
      key: "assignedUserEmail",
      render: (text) => text || "-",
    },
    {
      title: "Lead Source",
      dataIndex: "leadSource",
      key: "leadSource",
      render: (text) => text || "-",
    },
    {
      title: "Health",
      dataIndex: "lastHealthCheckResult",
      key: "lastHealthCheckResult",
      render: (result) =>
        result ? (
          <Tag color={result === "pass" ? "green" : "red"} className="uppercase">
            {result}
          </Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Last Used",
      dataIndex: "lastUsedAt",
      key: "lastUsedAt",
      render: (date) => (date ? formatDateTime(date) : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {isUnhealthy(record) && record.status !== "suspended" && record.status !== "retired" && (
            <Button
              type="primary"
              size="small"
              loading={replacingId === record._id}
              onClick={() => setReplaceTarget(record)}
            >
              Replace IP
            </Button>
          )}
          {(record.status === "suspended" || record.status === "retired") && (
            <Button size="small" loading={loading} onClick={() => handleReactivate(record)}>
              Reactivate
            </Button>
          )}
          <Dropdown menu={{ items: getMenuItems(record) }} trigger={["click"]}>
            <button
              type="button"
              className="px-2 py-1 rounded hover:bg-gray-100 text-primary"
            >
              <MoreOutlined />
            </button>
          </Dropdown>
        </div>
      ),
    },
  ];

  const tableData = (staticResidentialIps || []).map((item) => ({
    ...item,
    key: item._id,
  }));

  if (!tableData.length) {
    return <NoRecordFound message="No static residential IPs found." />;
  }

  return (
    <>
      {viewType === "LIST" ? (
        <CommonTable
          columns={columns}
          data={tableData}
          pagination={false}
          onRowClick={openDetails}
        />
      ) : (
        <EnhancedStaticResidentialIpsGridView
          data={tableData}
          onCardClick={openDetails}
          onEdit={(record) => setEditTarget(record)}
          onAssignClick={() => onAssignClick?.()}
          onUnassign={(record) => setUnassignTarget(record)}
          onReassign={(record) => setReassignTarget(record)}
          onHealthCheck={(record) => handleHealthCheck(record._id)}
          healthCheckingId={healthCheckingId}
          onStatus={(record) => {
            setStatusTarget(record);
            statusForm.setFieldsValue({ status: record.status === "retired" ? "suspended" : record.status });
          }}
          onSuspend={(record) => setSuspendTarget(record)}
          onReplaceIp={(record) => setReplaceTarget(record)}
          onReactivate={handleReactivate}
          replacingId={replacingId}
          actionLoading={loading}
        />
      )}

      {details.show && (
        <CommonDrawer
          open={details.show}
          setOpen={() => setDetails({ show: false, data: null })}
          title="Static Residential IP Details"
          width="65%"
          children={<EnhancedStaticResidentialIpDetails detail={details.data} />}
        />
      )}

      <EnhancedAddStaticResidentialIp
        open={!!editTarget}
        setOpen={(open) => {
          if (!open) setEditTarget(null);
        }}
        mode="edit"
        editRecord={editTarget}
        onSuccess={() => {
          setEditTarget(null);
          refetch();
        }}
      />

      <CommonDialog
        open={!!replaceTarget}
        setOpen={() => setReplaceTarget(null)}
        title="Replace IP via Webshare"
        description={`Request a Webshare replacement for ${replaceTarget?.host}:${replaceTarget?.port}. Webshare processes this asynchronously (validating → processing → completed). The same record and user assignment are kept — only host/port update when complete.`}
        okButtonTitle="Replace IP"
        isConfirmButtonLoading={loading}
        onOk={handleReplaceIp}
        onCancel={() => setReplaceTarget(null)}
      />

      <CommonDialog
        open={!!suspendTarget}
        setOpen={() => setSuspendTarget(null)}
        title="Suspend IP"
        description={`Suspend ${suspendTarget?.host}:${suspendTarget?.port}? A failed Webshare health check is required. The record stays in the list and can be reactivated later.`}
        okButtonTitle="Suspend"
        isConfirmButtonLoading={loading}
        onOk={handleSuspend}
        onCancel={() => setSuspendTarget(null)}
      />

      <CommonDialog
        open={!!unassignTarget}
        setOpen={() => setUnassignTarget(null)}
        title="Unassign IP"
        description={`Free ${unassignTarget?.host}:${unassignTarget?.port} from ${unassignTarget?.assignedUserEmail || "this user"} and return it to the unassigned pool?`}
        okButtonTitle="Unassign"
        isConfirmButtonLoading={loading}
        onOk={handleUnassign}
        onCancel={() => setUnassignTarget(null)}
      />

      <CommonDialog
        open={!!statusTarget}
        setOpen={() => {
          setStatusTarget(null);
          statusForm.resetFields();
        }}
        title="Change Status"
        okButtonTitle="Update"
        isConfirmButtonLoading={loading}
        onOk={handleStatusUpdate}
        onCancel={() => {
          setStatusTarget(null);
          statusForm.resetFields();
        }}
      >
        <CommonForm
          form={statusForm}
          data={[
            {
              label: "Status",
              name: "status",
              inputType: SOURCES.FORMINPUTTYPES.SELECT,
              required: true,
              options: STATUS_OPTIONS,
              placeholder: "Select status",
              message: "Status is required",
              span: 24,
            },
          ]}
        />
      </CommonDialog>

      <CommonDialog
        open={!!reassignTarget}
        setOpen={() => {
          setReassignTarget(null);
          reassignForm.resetFields();
        }}
        title="Reassign from pool"
        description={`Replace ${reassignTarget?.host}:${reassignTarget?.port} for ${reassignTarget?.assignedUserEmail || "this user"} with the next unassigned IP from the pool. Choose why this IP needs to be fixed — that sets its new status (flagged or suspended).`}
        okButtonTitle="Reassign"
        isConfirmButtonLoading={loading}
        onOk={handleReassign}
        onCancel={() => {
          setReassignTarget(null);
          reassignForm.resetFields();
        }}
      >
        <CommonForm
          form={reassignForm}
          data={[
            {
              label: "Fix reason",
              name: "reason",
              inputType: SOURCES.FORMINPUTTYPES.SELECT,
              required: true,
              options: REASSIGN_REASON_OPTIONS,
              placeholder: "Why are you replacing this IP?",
              message: "Please select a fix reason",
              span: 24,
            },
          ]}
        />
      </CommonDialog>
    </>
  );
};

export default EnhancedStaticResidentialIpsList;
